/**
 * Paid agent creation credits (docs/ECONOMICS.md "Agent creation", docs/API.md "Agent creation"): `POST
 * /agents/creation` → a 1 USDG payment intent of kind `agent_creation`; `payments.index` confirms it and records a
 * creation credit (`agents.creation_credits`, one credit = one agent). The token-first launch (creation v2,
 * `creation-v2.ts`) consumes it. The interview that compiled a spec from three answers and the strategy card with its
 * preview backtest went with algo.boo's own spec language (lane BOO2).
 */
import { z } from 'zod'
import { Handle, IsoTimestamp, Uuid } from '../primitives.js'
import { PaymentIntentResult } from './billing.js'

/** Price of one agent creation (USDG, platform revenue). */
export const AGENT_CREATION_PRICE_USD = '1.00'

export const CreationCreditStatus = z.enum(['paid', 'consumed', 'refunded'])
export type CreationCreditStatus = z.output<typeof CreationCreditStatus>

export const CreationCredit = z.object({
  id: Uuid,
  status: CreationCreditStatus,
  paymentIntentId: Uuid,
  /** The agent the credit created (consumed). */
  agentHandle: Handle.nullable(),
  createdAt: IsoTimestamp,
  consumedAt: IsoTimestamp.nullable(),
})
export type CreationCredit = z.output<typeof CreationCredit>

/** GET /me/creation-credits. */
export const CreationCredits = z.object({ items: z.array(CreationCredit) })
export type CreationCredits = z.output<typeof CreationCredits>

/** POST /agents/creation: the payment intent and its wallet txs; `creditId` is set when it confirmed at once. */
export const CreationPayment = PaymentIntentResult.extend({ creditId: Uuid.nullable() })
export type CreationPayment = z.output<typeof CreationPayment>

export const CreditIdParams = z.object({ creditId: Uuid })
